import { Fragment, type ComponentProps, type ElementType, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "../cx";
import { Icon, type IconName } from "../icons/Icon";

/** Sidebar del sistema; `railed` = plegada a raíl (sk-sidebar--rail). */
export function Sidebar({ railed, className, ...rest }: { railed?: boolean | undefined } & HTMLAttributes<HTMLElement>) {
  return <nav className={cx("sk-sidebar", railed && "sk-sidebar--rail", className)} {...rest} />;
}
export function NavGroup({ children }: { children: ReactNode }) {
  return <div className="sk-nav-group">{children}</div>;
}
/**
 * Ítem de navegación. `as` permite pasar el Link del router (recibe `to`);
 * por defecto es un <a>. En raíl, el sistema enseña el tooltip con `label`.
 */
export function NavItem<E extends ElementType = "a">({ as, icon, label, count, hot, active, railed, className, ...rest }: {
  as?: E | undefined; icon: IconName; label: string; count?: ReactNode | undefined; hot?: boolean | undefined; active?: boolean | undefined; railed?: boolean | undefined;
} & Omit<ComponentProps<E>, "as" | "children">) {
  const Comp: ElementType = as ?? "a";
  return (
    <Comp className={cx("sk-nav-item", hot && "sk-hot", className)} aria-current={active ? "page" : undefined} data-nav={label} data-sk-tip={railed ? label : undefined} data-sk-tip-pos={railed ? "right" : undefined} {...rest}>
      <Icon name={icon} />
      <span className="sk-label-text">{label}</span>
      {count !== undefined && <span className={cx("sk-count", hot && "sk-count--hot")}>{count}</span>}
      {hot && <span className="sk-hotdot" />}
    </Comp>
  );
}
/** Usuario anclado al pie de la sidebar (abre el menú). */
export function NavUser({ initials, name, subtitle, open, railed, ...rest }: { initials: string; name: string; subtitle: string; open: boolean; railed?: boolean | undefined } & HTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className="sk-nav-user" aria-haspopup="menu" aria-expanded={open} data-nav={name} data-sk-tip={railed ? name : undefined} data-sk-tip-pos={railed ? "right" : undefined} {...rest}>
      <span className="sk-avatar">{initials}</span>
      <span className="sk-u"><b>{name}</b><span>{subtitle}</span></span>
      <span className="sk-chev">▴</span>
    </button>
  );
}
/** Línea de entorno; `bare` = sin borde superior (pie del acceso). */
export function Envline({ bare, className, children }: { bare?: boolean | undefined; className?: string | undefined; children: ReactNode }) {
  return <div className={cx("sk-envline", bare && "sk-envline--bare", className)}><span className="sk-dot" /><span className="sk-label-text">{children}</span></div>;
}
/** Botón flotante de plegar/desplegar la nav (el chevron gira en raíl). */
export function NavFab({ railed, ...rest }: { railed: boolean } & HTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" className="sk-navfab" aria-pressed={railed} aria-label="Plegar o desplegar la navegación" data-sk-tip={railed ? "Desplegar la navegación" : "Plegar la navegación"} data-sk-tip-pos="right" {...rest}>
      <Icon name="chevron-l" style={railed ? { transform: "rotate(180deg)" } : undefined} />
    </button>
  );
}
export function Topbar({ children }: { children: ReactNode }) {
  return <header className="sk-topbar">{children}</header>;
}
/** Migas: separador y crumb como hijos directos de .sk-crumbs (flex con gap); el último en negrita. */
export function Crumbs({ items }: { items: ReactNode[] }) {
  return (
    <div className="sk-crumbs">
      {items.map((it, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="sk-crumb-sep">/</span>}
          {i === items.length - 1 ? <b>{it}</b> : it}
        </Fragment>
      ))}
    </div>
  );
}
export function SearchButton({ shortcut = "⌘K", children = "Buscar o ejecutar…", ...rest }: { shortcut?: string | undefined; children?: ReactNode } & HTMLAttributes<HTMLButtonElement> & { disabled?: boolean | undefined }) {
  return <button type="button" className="sk-searchbtn" {...rest}>{children} <span className="sk-kbd">{shortcut}</span></button>;
}
/** Conmutador de tema del Taller (◐): alterna sk-light en <body>. */
export function ThemeButton(props: HTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--s" aria-label="Cambiar tema" style={{ fontSize: "var(--sk-fs-m)" }} {...props}>◐</button>;
}
export function Popover({ open, className, ...rest }: { open: boolean } & HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("sk-popover", open && "sk-open", className)} role="menu" {...rest} />;
}
export function PopHead({ title, subtitle }: { title: ReactNode; subtitle: ReactNode }) {
  return <div className="sk-pop-head"><b>{title}</b><span>{subtitle}</span></div>;
}
export function MenuItem({ icon, danger, children, ...rest }: { icon?: IconName | undefined; danger?: boolean | undefined; children: ReactNode } & HTMLAttributes<HTMLButtonElement> & { disabled?: boolean | undefined }) {
  return <button type="button" className={cx("sk-menu-item", danger && "sk-danger")} {...rest}>{icon && <Icon name={icon} />}{children}</button>;
}
export const MenuSep = () => <div className="sk-menu-sep" />;
